import { Fragment } from 'react'
import {
  faCheck,
  faHouse,
  faRightLeft,
  faTriangleExclamation,
} from '@fortawesome/pro-duotone-svg-icons'

import Icon from '../icon/Icon'
import AffordabilityMeter from '../affordability-meter/AffordabilityMeter'
import CollectionPicker from '../collection-picker/CollectionPicker'
import ListingTagChip from '../listing-tag-chip/ListingTagChip'
import MatchScoreBadge from '../match-score-badge/MatchScoreBadge'
import {
  buildMetaParts,
  formatLastChecked,
  formatPrice,
  formatSourceSiteLabel,
} from './helpers'
import {
  Card,
  Hero,
  HeroPlaceholder,
  SiteBadge,
  ScoreCorner,
  CompareToggle,
  Body,
  Title,
  Price,
  MetaRow,
  MetaDivider,
  Footer,
  FooterStamp,
  FooterActions,
  IconButton,
  ScrapeWarning,
  VisitLink,
} from './styled'
import { ImportedListingCardProps } from './types'

/**
 * Renders a single imported listing as a card with hero image, title,
 * price, beds/baths/city meta, source-site badge, and footer with last-
 * checked stamp + refresh / remove / visit actions.
 *
 * @param {ImportedListingCardProps} props
 * @returns {JSX.Element}
 */
const ImportedListingCard = ({
  listing,
  onRemove,
  onRefresh,
  isRefreshing = false,
  matchScore = null,
  isCompareSelected = false,
  onToggleCompare,
}: ImportedListingCardProps): JSX.Element => {
  const heroImageUrl =
    listing.imageUrls.length > 0 ? listing.imageUrls[0] : null

  const metaParts = buildMetaParts(listing)
  const isScrapeIncomplete = listing.scrapeStatus !== 'success'

  return (
    <Card>
      <Hero
        style={
          heroImageUrl !== null
            ? { backgroundImage: `url(${heroImageUrl})` }
            : undefined
        }
      >
        {heroImageUrl === null ? (
          <HeroPlaceholder>
            <Icon icon={faHouse} />
          </HeroPlaceholder>
        ) : null}
        <SiteBadge>{formatSourceSiteLabel(listing.sourceSite)}</SiteBadge>
        {matchScore !== null ? (
          <ScoreCorner>
            <MatchScoreBadge score={matchScore} size="sm" />
          </ScoreCorner>
        ) : null}
        {onToggleCompare !== undefined ? (
          <CompareToggle
            type="button"
            isSelected={isCompareSelected}
            onClick={() => onToggleCompare(listing.id)}
            aria-label={
              isCompareSelected === true
                ? 'Remove from comparison'
                : 'Add to comparison'
            }
            title="Tap to compare with another listing"
          >
            <Icon icon={isCompareSelected === true ? faCheck : faRightLeft} />
          </CompareToggle>
        ) : null}
      </Hero>

      <Body>
        <Title to={`/imported-listings/${listing.id}`}>{listing.title}</Title>
        <Price>
          {formatPrice(listing.priceMonthly, listing.priceCurrency)}
        </Price>
        <AffordabilityMeter monthlyRent={listing.priceMonthly} size="sm" />

        {metaParts.length > 0 ? (
          <MetaRow>
            {metaParts.map((part, index) => (
              <Fragment key={`${part}-${index}`}>
                {index > 0 ? <MetaDivider>·</MetaDivider> : null}
                <span>{part}</span>
              </Fragment>
            ))}
          </MetaRow>
        ) : null}

        {isScrapeIncomplete === true ? (
          <ScrapeWarning>
            <Icon icon={faTriangleExclamation} />{' '}
            {listing.scrapeStatus === 'site-unknown'
              ? 'No parser for this site yet — basic info only.'
              : 'Scrape returned partial data — refresh to retry.'}
          </ScrapeWarning>
        ) : null}

        <Footer>
          <ListingTagChip listingId={listing.id} size="sm" />
          <CollectionPicker listingId={listing.id} size="sm" />
          <FooterActions>
            <VisitLink
              href={listing.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Visit ↗
            </VisitLink>
            <IconButton
              type="button"
              onClick={() => onRefresh(listing.id)}
              disabled={isRefreshing === true}
            >
              {isRefreshing === true ? 'Refreshing…' : 'Refresh'}
            </IconButton>
            <IconButton
              type="button"
              tone="danger"
              onClick={() => onRemove(listing.id)}
            >
              Remove
            </IconButton>
          </FooterActions>
        </Footer>
        <FooterStamp>
          Checked {formatLastChecked(listing.lastScrapedAt)}
        </FooterStamp>
      </Body>
    </Card>
  )
}

export default ImportedListingCard
